/**
 * AnswerReveal — the finished answer, arriving rather than appearing.
 *
 * Wraps whichever renderer `ResearchResponseView` chose and sequences its
 * rendered blocks in, one after the next, on the same fade-and-rise curve the
 * interim lines and the activity timeline use.
 *
 * Three rules it must not break, all of them enforced here rather than by the
 * renderers:
 *
 *   • ONLY ON LIVE ARRIVAL. `active` is true for a turn that completed in
 *     this session; a chat reopened from history renders flat. The reveal
 *     also runs at most ONCE per mount, so a re-render (a version fetch, a
 *     rating, a pubsub frame) never re-animates an answer already on screen.
 *   • NEVER BLOCKS READING. The reveal is skipped the moment the user shows
 *     intent — a scroll, a click, a key, a wheel — and finishes instantly.
 *   • NEVER OWNS THE CONTENT. Children are rendered in full from the first
 *     frame; this only sets opacity/transform on the resulting nodes, so no
 *     markdown is ever parsed half-written and nothing depends on the
 *     animation to become visible (a thrown-away frame leaves readable text).
 */

import React, { useEffect, useLayoutEffect, useRef } from 'react';
import {
  REVEAL_LEAD_MS,
  REVEALING_ATTR,
  clearBlock,
  hideBlock,
  prefersReducedMotion,
  revealBlocks,
  revealTiming,
  showBlock,
} from '../answerReveal';

interface Props {
  /** The turn completed live, in this session. */
  active: boolean;
  children: React.ReactNode;
}

/** Events that mean "I am reading now" — any of them ends the reveal. */
const SKIP_EVENTS = ['wheel', 'touchmove', 'pointerdown', 'keydown'] as const;

const AnswerReveal: React.FC<Props> = ({ active, children }) => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  /** One reveal per mount, whatever `active` does afterwards. */
  const ranRef = useRef(false);

  // Layout effect, not effect: the blocks must be hidden in the SAME frame
  // they are inserted, or the answer flashes at full opacity first — which is
  // the very jump this exists to remove.
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!active || !root || ranRef.current) return;
    if (prefersReducedMotion()) return;

    const blocks = revealBlocks(root);
    // Nothing recognisable to sequence (an image, a bare card): leave it be.
    if (blocks.length === 0) return;

    ranRef.current = true;
    /*
      The renderers' OWN entrances are switched off for the duration.

      Every typed renderer wraps its output in `animate-fade-in` and puts
      `.reveal-item` on its rows. Both are CSS animations, both outrank the
      inline styles below, and the container one fades the whole answer in as
      a single half-second block — which is exactly the "it just pastes in"
      this component exists to prevent. `hideBlock` drops the per-row
      animation; this attribute lets one stylesheet rule drop the container's.
    */
    root.setAttribute(REVEALING_ATTR, 'true');
    blocks.forEach(hideBlock);

    const { step, duration } = revealTiming(blocks.length);
    const timers: number[] = [];
    let finished = false;

    const finish = () => {
      if (finished) return;
      finished = true;
      timers.forEach((id) => window.clearTimeout(id));
      blocks.forEach(clearBlock);
      root.removeAttribute(REVEALING_ATTR);
      SKIP_EVENTS.forEach((name) => window.removeEventListener(name, finish));
      window.removeEventListener('scroll', finish, true);
    };

    blocks.forEach((node, i) => {
      timers.push(
        window.setTimeout(() => showBlock(node, duration), REVEAL_LEAD_MS + i * step)
      );
    });
    // Hand the nodes back once the last fade has run its course.
    timers.push(
      window.setTimeout(finish, REVEAL_LEAD_MS + blocks.length * step + duration + 60)
    );

    // `scroll` is captured: the transcript scrolls, not the window.
    SKIP_EVENTS.forEach((name) => window.addEventListener(name, finish, { passive: true }));
    window.addEventListener('scroll', finish, true);

    return finish;
  }, [active]);

  // A turn that unmounts mid-reveal (chat switched, version swapped) must not
  // leave a listener behind; the layout effect's cleanup covers it, and this
  // guard keeps the intent explicit for anything added later.
  useEffect(() => () => { ranRef.current = true; }, []);

  return (
    <div ref={rootRef} data-testid="answer-reveal" data-revealing={active || undefined}>
      {children}
    </div>
  );
};

export default AnswerReveal;
